import { LAYER_CONFIGS } from '../constants/general';

const getTitleBarHeight = (canvasHeight, titleText) => {
  if (!titleText) return 0;
  const fontSize = Math.max(16, Math.round(canvasHeight * 0.03));
  const padding = 12;
  return fontSize + padding * 2;
};

const drawCompass = (ctx, canvasWidth, canvasHeight, bearing, titleText) => {
  const titleBarHeight = getTitleBarHeight(canvasHeight, titleText);
  const margin = 28;
  const radius = Math.max(16, Math.round(Math.min(canvasWidth, canvasHeight) * 0.04));
  const centerX = canvasWidth - margin - radius;
  const centerY = titleBarHeight + margin + radius;

  ctx.save();

  // Compass background.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.82)';
  ctx.strokeStyle = 'rgba(31, 41, 55, 0.7)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Rotate opposite map bearing so marker points to true north on-screen.
  ctx.translate(centerX, centerY);
  ctx.rotate((-bearing * Math.PI) / 180);
  ctx.translate(-centerX, -centerY);

  // North marker: high-contrast triangle with an indented back edge.
  const arrowHeight = Math.max(10, Math.round(radius * 1.02));
  const tipY = centerY - arrowHeight / 2;
  const baseY = centerY + arrowHeight / 2;
  const halfWidth = Math.max(6, Math.round(radius * 0.42));
  const notchDepth = Math.max(3, Math.round(radius * 0.18));

  ctx.fillStyle = '#111827';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(centerX, tipY); // tip
  ctx.lineTo(centerX + halfWidth, baseY); // right base
  ctx.lineTo(centerX, baseY - notchDepth); // indented back edge
  ctx.lineTo(centerX - halfWidth, baseY); // left base
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.restore();
};

const toRadians = value => (value * Math.PI) / 180;

const haversineDistanceMeters = (lngLatA, lngLatB) => {
  const earthRadiusMeters = 6371008.8;
  const dLat = toRadians(lngLatB.lat - lngLatA.lat);
  const dLng = toRadians(lngLatB.lng - lngLatA.lng);
  const lat1 = toRadians(lngLatA.lat);
  const lat2 = toRadians(lngLatB.lat);

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMeters * c;
};

const getNiceDistanceMeters = targetMeters => {
  if (targetMeters <= 0) return 0;
  const exponent = Math.floor(Math.log10(targetMeters));
  const base = 10 ** exponent;
  const candidates = [1, 2, 5].map(multiplier => multiplier * base);

  for (let i = candidates.length - 1; i >= 0; i--) {
    if (candidates[i] <= targetMeters) {
      return candidates[i];
    }
  }

  return base / 2;
};

const formatScaleLabel = meters => {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }

  const kilometers = meters / 1000;
  if (kilometers >= 10) {
    return `${Math.round(kilometers)} km`;
  }

  return `${kilometers.toFixed(1)} km`;
};

const drawScaleBar = (
  ctx,
  map,
  sourceX,
  sourceY,
  sourceWidth,
  sourceHeight,
  pixelRatioX,
  pixelRatioY,
) => {
  const centerCanvasX = sourceX + sourceWidth / 2;
  const centerCanvasY = sourceY + sourceHeight / 2;

  const centerCssX = centerCanvasX / pixelRatioX;
  const centerCssY = centerCanvasY / pixelRatioY;
  const sampleCssX = centerCssX + 100;

  const centerLngLat = map.unproject([centerCssX, centerCssY]);
  const sampleLngLat = map.unproject([sampleCssX, centerCssY]);
  const metersPerCssPixel = haversineDistanceMeters(centerLngLat, sampleLngLat) / 100;
  const metersPerOutputPixel = metersPerCssPixel / pixelRatioX;

  if (!Number.isFinite(metersPerOutputPixel) || metersPerOutputPixel <= 0) {
    return;
  }

  const scaleLineSize = 0.75;
  const scaleFontSize = 0.5;
  const targetBarPixels = 220 * scaleLineSize;
  const targetMeters = targetBarPixels * metersPerOutputPixel;
  const niceDistanceMeters = getNiceDistanceMeters(targetMeters);
  const barWidth = niceDistanceMeters / metersPerOutputPixel;

  const insetX = 44;
  const insetY = 44;
  const x = insetX;
  const y = sourceHeight - insetY;
  const tickHeight = 16 * scaleLineSize;
  const labelFontSize = 36 * scaleFontSize;

  ctx.save();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.82)';
  ctx.fillRect(
    x - 12 * scaleLineSize,
    y - 46 * scaleLineSize,
    barWidth + 24 * scaleLineSize,
    54 * scaleLineSize,
  );

  ctx.strokeStyle = '#111827';
  ctx.lineWidth = 2 * scaleLineSize;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + barWidth, y);
  ctx.moveTo(x, y);
  ctx.lineTo(x, y - tickHeight);
  ctx.moveTo(x + barWidth, y);
  ctx.lineTo(x + barWidth, y - tickHeight);
  ctx.stroke();

  ctx.fillStyle = '#111827';
  ctx.font = `bold ${labelFontSize}px sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  ctx.fillText(formatScaleLabel(niceDistanceMeters), x, y - tickHeight - 4 * scaleFontSize);
  ctx.restore();
};

const getLegendItems = visibleLayerIds =>
  visibleLayerIds
    .map(layerId => ({ layerId, config: LAYER_CONFIGS[layerId] }))
    .filter(({ config }) => Boolean(config))
    .map(({ layerId, config }) => ({
      layerId,
      label: config.name,
      color:
        config.colors?.fillColor ?? config.style?.fillColor ?? config.style?.color ?? '#9ca3af',
    }));

const drawLegend = (ctx, canvasWidth, canvasHeight, visibleLayerIds) => {
  const items = getLegendItems(visibleLayerIds);
  if (!items.length) return;

  const margin = 16;
  const rowHeight = 20;
  const swatchSize = 10;
  const boxPadding = 10;

  ctx.save();
  ctx.font = '12px sans-serif';
  const maxLabelWidth = items.reduce(
    (max, item) => Math.max(max, ctx.measureText(item.label).width),
    0,
  );

  const boxWidth = Math.ceil(boxPadding * 2 + swatchSize + 8 + maxLabelWidth);
  const boxHeight = Math.ceil(boxPadding * 2 + items.length * rowHeight);
  const x = canvasWidth - margin - boxWidth;
  const y = canvasHeight - margin - boxHeight;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.strokeStyle = 'rgba(55, 65, 81, 0.45)';
  ctx.lineWidth = 1;
  ctx.fillRect(x, y, boxWidth, boxHeight);
  ctx.strokeRect(x, y, boxWidth, boxHeight);

  ctx.fillStyle = '#111827';
  ctx.font = 'bold 13px sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  ctx.font = '12px sans-serif';
  items.forEach((item, index) => {
    const rowY = y + boxPadding + index * rowHeight;

    ctx.fillStyle = item.color;
    ctx.fillRect(x + boxPadding, rowY + 4, swatchSize, swatchSize);
    ctx.strokeStyle = 'rgba(31, 41, 55, 0.4)';
    ctx.lineWidth = 0.75;
    ctx.strokeRect(x + boxPadding, rowY + 4, swatchSize, swatchSize);

    ctx.fillStyle = '#111827';
    ctx.fillText(item.label, x + boxPadding + swatchSize + 8, rowY + 2);
  });

  ctx.restore();
};

export const captureMap = async (
  mapRef,
  titleText,
  format,
  cropBounds,
  visibleLayerIds,
  basemap,
) => {
  const map = mapRef.current?.getMap?.() ?? mapRef.current;
  if (!map) throw new Error('Map instance not available');

  map.triggerRepaint();

  // Wait one frame for the repaint to complete
  await new Promise(resolve => requestAnimationFrame(resolve));

  const mapCanvas = map.getCanvas();
  const { width: mapWidth, height: mapHeight } = mapCanvas;
  const container = map.getContainer();
  const containerWidth = container?.clientWidth || mapWidth;
  const containerHeight = container?.clientHeight || mapHeight;
  const pixelRatioX = mapWidth / containerWidth;
  const pixelRatioY = mapHeight / containerHeight;

  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = mapWidth;
  let sourceHeight = mapHeight;

  if (cropBounds) {
    const { west, south, east, north } = cropBounds;
    const northWest = map.project([west, north]);
    const southEast = map.project([east, south]);

    // map.project returns CSS pixels; convert to canvas pixels for drawImage crop rect.
    const rawX = Math.min(northWest.x, southEast.x) * pixelRatioX;
    const rawY = Math.min(northWest.y, southEast.y) * pixelRatioY;
    const rawWidth = Math.abs(southEast.x - northWest.x) * pixelRatioX;
    const rawHeight = Math.abs(southEast.y - northWest.y) * pixelRatioY;

    sourceX = Math.max(0, Math.min(mapWidth, rawX));
    sourceY = Math.max(0, Math.min(mapHeight, rawY));
    sourceWidth = Math.max(0, Math.min(mapWidth - sourceX, rawWidth));
    sourceHeight = Math.max(0, Math.min(mapHeight - sourceY, rawHeight));

    // Fallback to full map if projected crop is invalid/tiny.
    if (sourceWidth < 2 || sourceHeight < 2) {
      sourceX = 0;
      sourceY = 0;
      sourceWidth = mapWidth;
      sourceHeight = mapHeight;
    }
  }

  // Create a composite canvas to draw map + title onto
  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = sourceWidth;
  outputCanvas.height = sourceHeight;
  const ctx = outputCanvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context not available');

  // Draw the map WebGL canvas as the base (cropped if cropBounds exist).
  ctx.drawImage(
    mapCanvas,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    sourceWidth,
    sourceHeight,
  );

  let barHeight = 0;
  // Draw title overlay if provided
  if (titleText) {
    const padding = 12;
    const fontSize = Math.max(16, Math.round(sourceHeight * 0.03)); // scale with map size
    ctx.font = `bold ${fontSize}px sans-serif`;

    barHeight = fontSize + padding * 2;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.fillRect(0, 0, sourceWidth, barHeight);

    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(titleText, sourceWidth / 2, barHeight / 2);
  }

  const dateInfo = `${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
  const sourceInfo = 'Data from https://tdis.io';

  const textColor = basemap === 'dark' || basemap === 'satellite' ? '#ffffff' : '#000000';

  const dateInfoX = 10;
  const dateInfoY = barHeight + 20;
  ctx.fillStyle = textColor;
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'left';
  ctx.font = '14px sans-serif';
  ctx.fillText(dateInfo, dateInfoX, dateInfoY);

  const sourceInfoX = 10;
  const sourceInfoY = barHeight + 40;
  ctx.fillStyle = textColor;
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'left';
  ctx.font = '14px sans-serif';
  ctx.fillText(sourceInfo, sourceInfoX, sourceInfoY);

  drawCompass(ctx, sourceWidth, sourceHeight, map.getBearing(), titleText);
  drawScaleBar(ctx, map, sourceX, sourceY, sourceWidth, sourceHeight, pixelRatioX, pixelRatioY);
  drawLegend(ctx, sourceWidth, sourceHeight, visibleLayerIds);

  const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
  const quality = format === 'jpeg' ? 0.95 : undefined;
  return outputCanvas.toDataURL(mimeType, quality);
};

export const initDownload = (dataUrl, name) => {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([array], { type: mime });
  const url = URL.createObjectURL(blob);
  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  downloadLink.download = name;
  downloadLink.click();
  URL.revokeObjectURL(url);
};
